import { useState, useEffect } from "react";
import { useNavigate } from "@/router";
import { PropsWithChildren } from "react";
import { LitPrimaryTab } from "./LitTabs";
import Icon from "../Icon";
import { MaterialNames } from "@/components/material_symbol_names";
import { DOMAIN } from "@/hooks/useCapabilities";
import BottomDrawer from "../BottomDrawer";
import NavItem from "../NavItem";

import classNames from "classnames";

export interface TabsProps {
  className?: string;
  tabItems?: {
    domain: DOMAIN;
    label: string;
    onClick?(): void;
    link: any;
    icon: MaterialNames;
    active?: boolean;
  }[];
  domains: DOMAIN[];
}

const customStyles = {
  "--md-primary-tab-active-indicator-color": "transparent",
  "--_with-icon-and-label-text-container-height": "auto",
  "--md-primary-tab-pressed-state-layer-opacity": "0",
  "--md-ripple-hover-opacity": "0",
  "--_container-color": "transparent",
  paddingLeft: "4px",
  paddingRight: "4px"
} as React.CSSProperties;

const safeAreaBottomStyles = {
  // iOS browsers (including Chrome) can reserve a bottom safe-area for the
  // home indicator, which can make fixed bottom UI appear "floating".
  // We shift the container down into that area while adding internal
  // padding so content stays readable.
  bottom: "calc(0px - env(safe-area-inset-bottom))",
  paddingBottom: "env(safe-area-inset-bottom)"
} as React.CSSProperties;

function getScrollDirection(setIsScrollDown: (isScrollDown: boolean) => void) {
  const condensedThreshold = 80; // Threshold to condense (hide icons when scrolling down)
  let ticking = false;
  let lastState = false; // false = expanded, true = condensed
  let lastScrollY = window.scrollY;
  let scrollDirection: "up" | "down" | null = null;
  let stateChangeCount = 0;
  let lastStateChangeTime = 0;

  const updateScrollState = () => {
    const currentScrollY = window.scrollY;
    const now = Date.now();

    // Check if we're at or near the bottom of the page
    const documentHeight = document.documentElement.scrollHeight;
    const windowHeight = window.innerHeight;
    const isNearBottom = currentScrollY + windowHeight >= documentHeight - 50; // 50px buffer

    // Determine scroll direction
    if (currentScrollY > lastScrollY) {
      scrollDirection = "down";
    } else if (currentScrollY < lastScrollY) {
      scrollDirection = "up";
    }

    // Reset state change count if enough time has passed
    if (now - lastStateChangeTime > 1000) {
      stateChangeCount = 0;
    }

    // Prevent rapid state changes (more than 3 in 1 second indicates flutter)
    if (stateChangeCount >= 3) {
      ticking = false;
      lastScrollY = currentScrollY;
      return;
    }

    // If we're near the bottom, don't change state to prevent bouncing
    if (isNearBottom) {
      ticking = false;
      lastScrollY = currentScrollY;
      return;
    }

    let shouldChangeState = false;

    // Only update state if we cross the appropriate threshold AND are scrolling in the right direction
    if (lastState && scrollDirection === "up") {
      // Expand: show full bar immediately when scrolling up (after condensed state)
      shouldChangeState = true;
      setIsScrollDown(false);
      lastState = false;
    } else if (!lastState && currentScrollY > condensedThreshold && scrollDirection === "down") {
      // Condense: hide icons when scrolling down past condense threshold
      shouldChangeState = true;
      setIsScrollDown(true);
      lastState = true;
    }

    if (shouldChangeState) {
      stateChangeCount++;
      lastStateChangeTime = now;
    }

    lastScrollY = currentScrollY;
    ticking = false;
  };

  const onScroll = () => {
    if (!ticking) {
      window.requestAnimationFrame(updateScrollState);
      ticking = true;
    }
  };

  window.addEventListener("scroll", onScroll);

  return () => window.removeEventListener("scroll", onScroll);
}

export default function BottomBar({ className, tabItems, domains }: PropsWithChildren<TabsProps>) {
  const permittedLinks = tabItems?.filter(
    (navLink) => navLink.domain === DOMAIN.EVERYONE || domains.includes(navLink.domain)
  );
  const navigate = useNavigate();

  const [isScrollDown, setIsScrollDown] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const maxVisibleTabs = 3;
  const hasOverflow = (permittedLinks?.length || 0) > maxVisibleTabs;
  const visibleLinks = hasOverflow ? permittedLinks?.slice(0, maxVisibleTabs) : permittedLinks;
  const overflowLinks = hasOverflow ? permittedLinks?.slice(maxVisibleTabs) : [];

  // Check if any overflow item is active
  const hasActiveOverflowItem = overflowLinks?.some((link) => link.active) || false;

  useEffect(() => {
    getScrollDirection(setIsScrollDown);
  }, [isScrollDown]);

  return (
    <div
      className={`bg-sys-brand-surface shadow-web-3 rounded-2xl fixed bottom-0 left-0 z-50 w-full flex flex-col justify-between duration-700 ease-in-out px-3 ${
        className ?? ""
      } transition-all`}
      style={safeAreaBottomStyles}
      data-testid="bottom-bar"
    >
      <div
        className={`grid grid-flow-col auto-cols-auto justify-center items-baseline transition-all duration-700 ease-in-out my-4`}
      >
        {visibleLinks?.map(({ label, link, icon, active }, i) => {
          const formattedLabel = label.replace(/ and /g, " & ");
          return (
            <LitPrimaryTab
              key={i}
              active={active}
              onClick={() => navigate(link.to, { params: link.params })}
              data-testid="tab"
              hasIcon={true}
              style={customStyles}
            >
              <div
                className={`flex flex-col items-center justify-center transition-all duration-700 ease-in-out p-2 px-4 ${
                  active ? "border border-sys-brand-primary rounded-xl" : ""
                }`}
              >
                {!isScrollDown && (
                  <Icon
                    name={icon as MaterialNames}
                    size={24}
                    className={classNames("transition-all duration-700 ease-in-out", {
                      "text-sys-brand-primary": active,
                      "text-sys-brand-on-surface": !active
                    })}
                  />
                )}
                <div
                  className={classNames(
                    "text-title-medium whitespace-normal text-center transition-all duration-700 ease-in-out",
                    "text-title-medium-bold font-serif",
                    {
                      "text-sys-brand-primary": active,
                      "text-sys-brand-on-surface": !active
                    }
                  )}
                >
                  {formattedLabel}
                </div>
              </div>
            </LitPrimaryTab>
          );
        })}

        {hasOverflow && (
          <LitPrimaryTab
            key="more"
            active={hasActiveOverflowItem}
            onClick={() => setIsDrawerOpen(true)}
            data-testid="more-tab"
            hasIcon={true}
            style={customStyles}
          >
            <div
              className={`flex flex-col items-center justify-center transition-all duration-700 ease-in-out p-2 px-4 ${
                hasActiveOverflowItem ? "border border-sys-brand-primary rounded-xl" : ""
              }`}
            >
              {!isScrollDown && (
                <Icon
                  name="pending"
                  size={24}
                  className={classNames("transition-all duration-700 ease-in-out", {
                    "text-sys-brand-primary": hasActiveOverflowItem,
                    "text-sys-brand-on-surface": !hasActiveOverflowItem
                  })}
                />
              )}
              <div
                className={classNames(
                  "text-title-medium whitespace-normal text-center transition-all duration-700 ease-in-out",
                  "text-title-medium-bold font-serif",
                  {
                    "text-sys-brand-primary": hasActiveOverflowItem,
                    "text-sys-brand-on-surface": !hasActiveOverflowItem
                  }
                )}
              >
                More
              </div>
            </div>
          </LitPrimaryTab>
        )}
      </div>

      <BottomDrawer
        isVisible={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        data-testid="bottom-bar-overflow-drawer"
      >
        <div className="space-y-2">
          <div className="flex items-center">
            <button
              onClick={() => setIsDrawerOpen(false)}
              className="p-1 hover:bg-sys-brand-surface-container-high rounded-full flex items-center justify-center"
              data-testid="close-drawer-button"
            >
              <Icon name="close" size={20} className="text-sys-brand-on-surface" />
            </button>
            <h3
              className={classNames(
                "text-title-large ml-2 text-sys-brand-on-surface",
                "text-title-large-bold font-serif"
              )}
            >
              More
            </h3>
          </div>
          {overflowLinks?.map(({ label, link, icon, active }, i) => {
            const formattedLabel = label.replace(/ and /g, " & ");
            return (
              <div key={i} className="w-full" data-testid={`overflow-tab-${i}`}>
                <NavItem
                  label={formattedLabel}
                  onClick={() => {
                    navigate(link.to, { params: link.params });
                    setIsDrawerOpen(false);
                  }}
                  active={active}
                  iconName={icon as MaterialNames}
                  orientation="horizontal"
                />
              </div>
            );
          })}
        </div>
      </BottomDrawer>
    </div>
  );
}
